// Coordinatore del client: tiene configurazione, scope, transport, log buffer,
// stats e dedup, e implementa la logica di cattura/invio. Specchio di
// `CloseYourIt::Client` (gem) e `CloseYourItClient` (dart). Dipendenze iniettabili
// per i test. Nessun metodo solleva.

import type { BreadcrumbInput } from './breadcrumbs'
import { Deduplicator, dedupKey } from './dedup'
import {
  buildErrorEvent,
  buildLogEvent,
  buildMessageEvent,
  normalizeError,
} from './events'
import { LOGS_MAX_BATCH, LogBuffer, chunkBatches } from './logBuffer'
import { meetsMinLevel, normalizeLogLevel } from './logLevel'
import { buildPerformanceIssueEvent, buildSlowMethodEvent } from './metricEvents'
import {
  disabledReason,
  endpointHost,
  eventsUri,
  isEnabled,
  logsUri,
  metricsUri,
  pageviewsUri,
  resolveOptions,
} from './options'
import { buildPageview } from './pageviews'
import { autoRequestContext } from './requestContext'
import type { ResolvedOptions } from './options'
import { isBrowser } from './runtime'
import { Scope } from './scope'
import { Stats } from './stats'
import { Transport } from './transport'
import type {
  CaptureHint,
  CloseYourItOptions,
  EventLevel,
  EventPayload,
  LogLevel,
  UserContext,
} from './types'
import { uuidV4 } from './uuid'

/// Dipendenze iniettabili (test).
export interface ClientDeps {
  transport?: Transport
  logBuffer?: LogBuffer
  stats?: Stats
  scope?: Scope
  dedup?: Deduplicator
  random?: () => number
  /// Provider del request context automatico (default: pagina corrente nel browser).
  requestProvider?: () => Record<string, unknown> | undefined
  /// Clock monotono in ms per `measure` (default `performance.now`).
  nowMs?: () => number
}

export class Client {
  readonly options: ResolvedOptions
  readonly stats: Stats
  readonly scope: Scope

  private readonly transport: Transport
  private readonly logBuffer: LogBuffer
  private readonly dedup: Deduplicator
  private readonly random: () => number
  private readonly requestProvider: () => Record<string, unknown> | undefined
  /// Clock monotono in ms (iniettabile): usato da `measure` e dalle integrazioni.
  readonly nowMs: () => number

  constructor(rawOptions: CloseYourItOptions, deps: ClientDeps = {}) {
    this.options = resolveOptions(rawOptions)
    this.stats = deps.stats ?? new Stats()
    this.scope = deps.scope ?? new Scope(this.options.maxBreadcrumbs)
    this.dedup = deps.dedup ?? new Deduplicator()
    this.random = deps.random ?? Math.random
    this.requestProvider =
      deps.requestProvider ?? (() => autoRequestContext(this.options.sendPii))
    this.nowMs = deps.nowMs ?? defaultNowMs

    this.transport =
      deps.transport ??
      new Transport({
        token: this.options.token,
        stats: this.stats,
        maxQueue: this.options.maxQueue,
        debug: this.options.debug ? (m, e) => this.debugLog(m, e) : undefined,
      })
    this.logBuffer =
      deps.logBuffer ??
      new LogBuffer(
        (batch) => this.flushLogs(batch),
        this.options.logsBatchSize,
        this.options.logsFlushIntervalMs,
      )

    // trace_id di pagina: correla errori e log della stessa sessione/pagina.
    if (this.scope.traceId === undefined) {
      this.scope.setTraceId(uuidV4().replace(/-/g, ''))
    }
  }

  get enabled(): boolean {
    return isEnabled(this.options)
  }

  get disabledReason(): string | null {
    return disabledReason(this.options)
  }

  get endpointHost(): string | undefined {
    return endpointHost(this.options)
  }

  get slowExternalThresholdMs(): number {
    return this.options.slowExternalThresholdMs
  }

  // ---------------------------------------------------------------- errori

  /// Cattura un'eccezione (o valore arbitrario). No-op se disabilitato, escluso,
  /// già catturato, duplicato entro la finestra, o non campionato.
  captureException(error: unknown, hint: CaptureHint = {}): void {
    if (!this.enabled) return

    const normalized = normalizeError(error)
    if (this.options.excludedExceptions.includes(normalized.type)) return
    if (this.dedup.wasCaptured(error)) return

    const key = dedupKey(
      normalized.type,
      normalized.value,
      topFrameKey(normalized.stack),
    )
    if (this.dedup.isDuplicate(key)) return
    // Marcato PRIMA del sampling (parità gem): un errore scartato dal sampling
    // non viene re-inviato da un handler più esterno.
    this.dedup.markCaptured(error)
    if (!this.sampled(this.options.sampleRate)) return

    const payload = buildErrorEvent({
      error: normalized,
      environment: this.options.environment,
      release: this.options.release,
      handled: hint.handled ?? false,
      level: hint.level ?? 'error',
      scopeHash: this.scopeHash(),
      traceId: this.scope.traceId,
      contexts: hint.contexts,
      request: this.autoRequest(),
    })
    this.enqueue(payload, eventsUri(this.options))
  }

  /// Cattura un messaggio diagnostico.
  captureMessage(message: string, level: EventLevel = 'info'): void {
    if (!this.enabled) return
    if (!this.sampled(this.options.sampleRate)) return

    const payload = buildMessageEvent({
      message,
      environment: this.options.environment,
      release: this.options.release,
      level,
      scopeHash: this.scopeHash(),
      traceId: this.scope.traceId,
      request: this.autoRequest(),
    })
    this.enqueue(payload, eventsUri(this.options))
  }

  // ------------------------------------------------------------------- logs

  /// Bufferizza un log strutturato (inviato in batch a `/logs`). No-op se i log
  /// sono disattivati, sotto `logsMinLevel`, non campionati o con message vuoto.
  log(
    level: LogLevel,
    message: string,
    attributes?: Record<string, unknown>,
    logger?: string,
  ): void {
    if (!this.enabled || !this.options.logsEnabled) return
    const normalized = normalizeLogLevel(level)
    if (!meetsMinLevel(normalized, this.options.logsMinLevel)) return
    if (!this.sampled(this.options.logsSampleRate)) return
    if (message.trim().length === 0) return

    this.logBuffer.add(
      buildLogEvent({
        message,
        level: normalized,
        environment: this.options.environment,
        release: this.options.release,
        logger,
        traceId: this.scope.traceId,
        attributes,
      }),
    )
  }

  // --------------------------------------------------------------- metriche

  /// Misura [body] e invia una metrica `slow_method` se la durata supera
  /// `slowMethodThresholdMs`. Preserva la sincronicità: un body sincrono
  /// ritorna il valore, un body async ritorna la Promise (misurata al settle).
  /// La metrica parte anche se il body lancia. MAI campionata.
  /// Label vuota → TypeError immediato anche a client disabilitato (errore del
  /// programmatore, deve emergere deterministicamente).
  measure<T>(
    label: string,
    body: () => T,
    opts: { args?: Record<string, unknown> } = {},
  ): T {
    if (typeof label !== 'string' || label.trim().length === 0) {
      throw new TypeError(
        'closeyourit: measure(label) richiede una label non vuota (è il fingerprint della metrica)',
      )
    }

    const start = this.nowMs()
    const finish = (): void => {
      const duration = this.nowMs() - start
      this.recordSlowMethod(label, duration, opts.args)
    }

    try {
      const result = body()
      if (isThenable(result)) {
        return (result as Promise<unknown>).then(
          (value) => {
            finish()
            return value
          },
          (error: unknown) => {
            finish()
            throw error
          },
        ) as T
      }
      finish()
      return result
    } catch (error) {
      finish()
      throw error
    }
  }

  /// Verdetto `slow_external_http` per una richiesta esterna completata.
  /// No-op sotto soglia o senza `detectPerformanceIssues`/`captureExternalHttp`.
  recordExternalHttp(host: string, url: string, durationMs: number): void {
    if (!this.perfEnabled() || !this.options.captureExternalHttp) return
    const threshold = this.options.slowExternalThresholdMs
    if (threshold <= 0 || durationMs < threshold) return

    this.enqueue(
      buildPerformanceIssueEvent({
        subtype: 'slow_external_http',
        durationMs,
        environment: this.options.environment,
        httpHost: host,
        httpUrl: url,
        traceId: this.scope.traceId,
      }),
      metricsUri(this.options),
    )
  }

  /// Verdetto `repeated_http`: stessa richiesta ripetuta [count] volte nella
  /// finestra (l'N+1 del client). Il tracking della finestra sta all'integrazione.
  recordRepeatedHttp(host: string, url: string, count: number): void {
    if (!this.perfEnabled() || !this.options.captureExternalHttp) return

    this.enqueue(
      buildPerformanceIssueEvent({
        subtype: 'repeated_http',
        environment: this.options.environment,
        httpHost: host,
        httpUrl: url,
        countInRequest: count,
        traceId: this.scope.traceId,
      }),
      metricsUri(this.options),
    )
  }

  /// Verdetto `jank`: long task del main thread oltre soglia.
  recordJank(durationMs: number, route: string): void {
    if (!this.perfEnabled() || !this.options.detectJank) return

    this.enqueue(
      buildPerformanceIssueEvent({
        subtype: 'jank',
        durationMs,
        environment: this.options.environment,
        route,
        traceId: this.scope.traceId,
      }),
      metricsUri(this.options),
    )
  }

  // --------------------------------------------------------------- pageview

  /// Cattura una pageview (web analytics cookieless). No-op se disabilitato o
  /// fuori dal browser. MAI campionata (parità con metriche/log: l'analytics
  /// perde di senso con dati parziali). [overrides.path] forza il path (default
  /// `location.pathname`); `environment` è aggiunto qui dall'ambiente risolto.
  /// Passa da `enqueue`, quindi rispetta `beforeSend` e i contatori come gli
  /// altri invii.
  capturePageview(overrides: { path?: string } = {}): void {
    if (!this.enabled) return
    if (!isBrowser()) return

    this.enqueue(
      { ...buildPageview(overrides), environment: this.options.environment },
      pageviewsUri(this.options),
    )
  }

  // ------------------------------------------------------------------ scope

  setUser(user: UserContext | null): void {
    this.scope.setUser(user)
  }

  setTag(key: string, value: string): void {
    this.scope.setTag(key, value)
  }

  setTags(tags: Record<string, string>): void {
    this.scope.setTags(tags)
  }

  setExtra(key: string, value: unknown): void {
    this.scope.setExtra(key, value)
  }

  setContext(key: string, value: Record<string, unknown>): void {
    this.scope.setContext(key, value)
  }

  setTraceId(id: string | undefined): void {
    this.scope.setTraceId(id)
  }

  addBreadcrumb(input: BreadcrumbInput): void {
    if (!this.options.breadcrumbs) return
    this.scope.addBreadcrumb(input)
  }

  clearScope(): void {
    this.scope.clear()
  }

  // ------------------------------------------------------------- lifecycle

  /// Svuota i log bufferizzati e attende gli invii in volo.
  async flush(): Promise<void> {
    this.logBuffer.flush()
    await this.transport.flush()
  }

  /// Spegne il client: svuota i log, attende gli invii.
  async close(): Promise<void> {
    this.logBuffer.dispose()
    await this.transport.flush()
  }

  // -------------------------------------------------------------- internals

  private enqueue(payload: EventPayload, url: string): void {
    const hook = this.options.beforeSend
    let body: EventPayload = payload
    if (hook) {
      const result = hook(payload)
      if (result === null) return
      body = result
    }
    if (this.transport.send(body, url)) this.stats.increment('enqueued')
  }

  private flushLogs(batch: EventPayload[]): void {
    if (batch.length === 0) return
    const hook = this.options.beforeSend
    let payloads = batch
    if (hook) {
      payloads = batch
        .map((event) => hook(event))
        .filter((event): event is EventPayload => event !== null)
      if (payloads.length === 0) return
    }
    // Il backend rifiuta batch oltre LOGS_MAX_BATCH (413): spezza in invii ≤ soglia.
    for (const chunk of chunkBatches(payloads, LOGS_MAX_BATCH)) {
      if (this.transport.send(chunk, logsUri(this.options))) {
        this.stats.increment('enqueued')
      }
    }
  }

  private recordSlowMethod(
    label: string,
    durationMs: number,
    args: Record<string, unknown> | undefined,
  ): void {
    if (!this.enabled) return
    if (durationMs < this.options.slowMethodThresholdMs) return

    this.enqueue(
      buildSlowMethodEvent({
        label,
        durationMs,
        environment: this.options.environment,
        traceId: this.scope.traceId,
        args: this.options.captureMethodArguments ? args : undefined,
      }),
      metricsUri(this.options),
    )
  }

  private perfEnabled(): boolean {
    return this.enabled && this.options.detectPerformanceIssues
  }

  private scopeHash(): EventPayload {
    return this.scope.toEventHash({ sendPii: this.options.sendPii })
  }

  private autoRequest(): Record<string, unknown> | undefined {
    if (!this.options.captureRequest) return undefined
    return this.requestProvider()
  }

  private sampled(rate: number): boolean {
    if (rate >= 1) return true
    if (rate <= 0) return false
    return this.random() < rate
  }

  // Log diagnostici interni su console (mai nel log buffer: eviterebbe loop di
  // telemetria su un invio fallito). Attivo solo con `debug: true`.
  private debugLog(message: string, error?: unknown): void {
    if (!this.options.debug) return
    if (typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.debug('[closeyourit]', message, error ?? '')
    }
  }
}

function isThenable(value: unknown): value is Promise<unknown> {
  return (
    value !== null &&
    (typeof value === 'object' || typeof value === 'function') &&
    typeof (value as { then?: unknown }).then === 'function'
  )
}

function defaultNowMs(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()
}

// Prima riga di stack utile (crash frame) per la chiave di dedup.
function topFrameKey(stack: string | undefined): string | undefined {
  if (!stack) return undefined
  for (const raw of stack.split('\n')) {
    const line = raw.trim()
    if (line.startsWith('at ') || line.includes('@')) return line
  }
  return undefined
}
